<template>
  <div class="site-render site-common" :class="renderClass">
    <PageRender
      v-if="currentPageRenderData"
      :renderData="currentPageRenderData"/>
    <Chatbot
      v-if="showChatbot && enableChatbot"
      :btnDialogToggleBootem="'24px'"
      :generateUserId="userId"
      :showChatbotMessageBox="showChatbotMessageBox"
      :company="company"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import _ from 'lodash'
import PageData from '../../PageData'
import PageRender from './pageRender'
import { createRootCssStyle } from '../../modules'
import { deviceType } from '@/utils/common'
import Chatbot from '@/components/job/chatbot/index.vue'
import { ACCOUNT_TYPE } from '@/constant/common'

export default {
  components: {
    PageRender,
    Chatbot
  },
  metaInfo () {
    return {
      title: this.currentPageTitle,
      style: this.cssStyleList,
      link: this.metaInfoLinkList
    }
  },
  data () {
    return {
      initSiteId: this.$route.params.siteId,
      theme: {},
      siteDataList: {},
      pathPrefix: '',
      renderClass: null,
      cssStyleList: [],
      metaInfoLinkList: [],
      enableChatbot: false,
      showChatbot: false,
      showChatbotMessageBox: false,
      userId: '',
      company: {
        id: null,
        root_company: null
      }
    }
  },
  computed: {
    ...mapGetters('microSite', [
      'isEditMode',
      'currentPage',
      'currentPageRenderData',
      'pagePathPrefix',
      'sitePages',
      'isMobile'
    ]),
    ...mapGetters('company', ['domainCompany']),
    currentPageTitle () {
      return this.currentPage && this.currentPage.metaInfo && this.currentPage.metaInfo.title
    },
    type () {
      return this.$route.params.type
    },
    siteId () {
      return this.$route.params.siteId
    },
    pagePath () {
      return this.$route.params.pagePath
    }
  },
  created () {
    this.pathPrefix = `/microsite-test/${this.siteId}${this.type && '/' + this.type}`
  },
  methods: {
    ...mapGetters('account', ['getCurrentAccount']),
    ...mapActions('microSite', [
      'getSiteDataById',
      'setEditMode',
      'resetRuntimeData',
      'setPagePathPrefix',
      'setEditMode',
      'setMobile',
      'setLanguageCode',
      'setSitePages',
      'setCurrentPage',
      'setCurrentPageType',
      'setThemes',
      'setCompanyBasicInfo'
    ]),
    async initSitePages () {
      this.resetRuntimeData()
      this.setEditMode(false)
      this.setPagePathPrefix(this.pathPrefix)
      this.setMobile(!deviceType.isPc)
      this.setLanguageCode(this.$i18n.locale === 'en' ? 'en' : 'cn')
      this.renderClass = [this.isMobile ? 'site-mobile' : 'site-pc', deviceType.isiOS && 'is-ios']
      try {
        const resp = await this.getSiteDataById({ id: this.siteId, type: this.type })
        if (resp) {
          const currentCompanyId = resp.originResponse.company.id
          if (this.domainCompany && this.domainCompany.children) {
            if (!_.includes(this.domainCompany.children, currentCompanyId)) {
              window.location.href = '/'
              return
            }
          }
          const currentAccount = this.getCurrentAccount()
          if (!this.isEditMode && resp.originResponse.need_login && !(currentAccount && currentAccount.id)) {
            this.$router.push({ path: `/passport/${ACCOUNT_TYPE.CANDIDATE}/register`, query: { company_id: currentCompanyId, back: this.$route.fullPath } })
            return
          }
          const sitePages = []
          this.siteDataList = resp
          const targetPagesAndTheme = this.getSiteDataByDeviceAndLocale(this.siteDataList)
          const targetPages = targetPagesAndTheme.pages
          const targetTheme = targetPagesAndTheme.theme
          _.forEach(targetPages, pageObj => {
            sitePages.push(PageData.parseObject(pageObj))
          })
          this.theme = targetTheme
          this.setSitePages(sitePages)
          this.refreshRenderPage()
          this.setThemes({ [this.siteId]: targetTheme })
          this.showChatbot = this.siteDataList.originResponse.company.show_chatbot
          this.enableChatbot = this.siteDataList.originResponse.enable_chatbot
          this.company = {
            id: this.siteDataList.originResponse.company.id,
            root_company: {
              id: this.siteDataList.originResponse.company.root_id
            }
          }
          this.setCompanyBasicInfo(this.company)
          this.cssStyleList = [
            {
              vmid: this.siteId,
              innerHTML: createRootCssStyle(this.theme.bgColor, this.theme.btnColor)
            }
          ]
          let baseUrl = process.env.VUE_APP_STYLE_BASE_URL
          if (baseUrl.endsWith('/')) {
            baseUrl = baseUrl.substring(0, baseUrl.length - 1)
          }
          const linkList = []
          if (this.showChatbot) {
            linkList.push({
              rel: 'stylesheet',
              href: `${baseUrl}/static/style/henkel-bot.css`
            })
          }
          this.metaInfoLinkList = linkList
        }
        return resp
      } catch (err) {
        if (this.handleResponseError) {
          this.handleResponseError(err)
        } else {
          throw err
        }
      }
    },
    refreshRenderPage () {
      let pagePath = this.pagePath
      if (!pagePath) {
        const pageData = _.find(this.sitePages, 'isIndex')
        if (pageData && pageData.status.show) {
          this.$router.replace({
            path: `${this.pagePathPrefix}${pageData.path}`,
            query: this.$route.query
          })
        } else {
          // to-do redirect to 404 page
        }
        return
      }
      if (!pagePath.startsWith('/')) {
        pagePath = '/' + pagePath
      }
      if (this.currentPage && this.currentPage.path === pagePath) {
        return
      }
      const pageData = _.find(this.sitePages, { path: pagePath })
      if (pageData && pageData.status.show) {
        this.setCurrentPage(pageData)
      } else {
        // to-do redirect to 404 page
      }
    },
    getSiteDataByDeviceAndLocale (siteDataList) {
      let deviceTryList = {
        pc: 1,
        mobile: 1
      }
      let localeTryList = {
        cn: 1,
        en: 1,
        tc: 1
      }
      if (this.isMobile) {
        deviceTryList.mobile = 2
      } else {
        deviceTryList.pc = 2
      }
      const locale = this.$i18n.locale
      if (locale === 'en') {
        localeTryList.en = 2
      } else if (locale === 'hk') {
        localeTryList.tc = 2
      } else if (locale === 'tw') {
        localeTryList.tc = 2
      } else {
        localeTryList.cn = 2
      }
      deviceTryList = this.sortTryList(deviceTryList)
      localeTryList = this.sortTryList(localeTryList)
      let siteData = null
      _.forEach(deviceTryList, device => {
        const deviceData = siteDataList[device]
        _.forEach(localeTryList, locale => {
          const localeData = deviceData[locale]
          if (localeData && localeData.pages) {
            siteData = localeData
            this.setCurrentPageType(`${device}_${locale}`)
            return false
          }
        })
        if (siteData) {
          return false
        }
      })
      return siteData || {}
    },
    sortTryList (tryList) {
      const temp = {}
      _.forEach(tryList, (value, key) => {
        if (temp.hasOwnProperty(value)) {
          temp[value].push(key)
        } else {
          temp[value] = [key]
        }
      })
      const target = []
      _.forEach(temp, (value, key) => {
        target.splice(0, 0, ..._.orderBy(value))
      })
      return target
    }
  },
  watch: {
    pagePath () {
      if (this.siteId !== this.initSiteId) {
        return
      }
      this.refreshRenderPage()
    },
    siteId () {
      if (this.$parent.reloadMicroSite) {
        this.$parent.reloadMicroSite()
      } else {
        // eslint-disable-next-line no-console
        console.error('MicroSite parent component should have a method to reload this MicroSite')
        if (process.env.VUE_APP_ENV !== 'production') {
          this.$message.error({
            message: 'MicroSite parent component should have a method to reload this MicroSite',
            showClose: true
          })
        }
      }
    }
  }
}
</script>

<style lang="scss">
.site-render {
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);
  box-sizing: border-box;
  * {
    box-sizing: border-box;
  }

  &.site-common {
    &:not(.edit-mode) {
      min-height: 100vh;
      min-height: 100dvh;

      .no-wrapper {
        display: none;
      }
    }

    .module-text {
      &-title {
        color: #333333;
        font-weight: bold;
      }

      &-subtitle {
        color: #333333;
        font-weight: bold;
      }

      &-content {
        color: #666666;
        text-align: center;

        &.ql-editor {
          color: #666666;

          .ql-align-left {
            text-align: left;
          }
        }
      }
    }
  }

  &.site-mobile {
    .module-text {
      &-title {
        font-size: 30px;
      }

      &-subtitle {
        font-size: 18px;
      }

      &-content {
        font-size: 14px;
      }
    }
  }

  &.site-pc {
    .microsite-root {
      height: 100vh;
      overflow: auto;
    }
    .module-text {
      &-title {
        font-size: 32px;
      }

      &-subtitle {
        font-size: 20px;
      }

      &-content {
        font-size: 16px;
      }
    }
  }
}
</style>
